<template>
    <div class="container">
        <h1>Qu'est-ce que Cardano ?</h1>

        <p>
            Cardano est <b>une blockchain de 3e génération</b> qui a été lancée en 2017. Ouroboros, son algorithme de consensus de type
            <a href="/doc-cardano/qu-est-ce-que-le-delegated-proof-of-stake-dpos" title="Qu'est-ce que le delegated proof of stake ?">Delegated Proof of
                Stake (DPoS)</a>, permet d'atteindre des performances inégalées, tout en maintenant un réseau décentralisé. En moins de 2 ans,
            Cardano s'est hissée dans le top10 des cryptomonnaies par capitalisation, selon coinmarketcap.
        </p>

        <h2 id="pourquoi-cardano">Cardano, en bref</h2>
        <p>
            Cardano a été fondée par <a href="https://en.wikipedia.org/wiki/Charles_Hoskinson"><i>Charles Hoskinson</i></a>, l'un des cofondateurs d'Ethereum.
            Il a présenté, lors de l'introduction du projet,
            l'objectif de Cardano, qui est avant tout d'apprendre des erreurs de Bitcoin et d'Ethereum, qui rencontrent d'important blocages sur
            des problématiques de scalabilité, de performances et de gouvernances. Souvent surnommée <i>l'Ethereum killer</i>, Cardano a pour but
            d'implémenter une blockchain dont le réseau et la gouvernance sont totalement décentralisée, tout en maintenant de haute performances
            (suffisantes pour devenir le principal système financier mondial), des frais de transactions très bas.
        </p>

        <p>
            En plus d'acquérir <a href="/articles/les-caracteristiques-d-une-cryptomonnaie-reussie">les différentes caractéristiques d'une blockchain réussie</a>,
            (ce qu'aucune blockchain n'a réussi à faire à l'heure actuelle), Cardano veut devenir <b>interopérable</b> avec les autres blockchains,
            mais aussi avec le monde de la finance classique et de l'industrie, grâce (entre autres) au <mark>smarts contracts</mark>.
        </p>

        <p>
            Financée à l'origine par une <b>ICO</b> de <b>62.5 millions de dollars</b>, Cardano est un projet de cryptomonnaie atypique aux fondations solidement
            universitaire: le développement du projet est basé intégralement sur des recherches, effectuées par les équipes de Cardano, qui sont
            <a href="https://iohk.io/en/research/library/">publiées</a> et <u>revues par les pairs</u>.
            Le code du projet est réalisé intégralement en <a href="https://fr.wikipedia.org/wiki/Haskell">Haskell</a>, un langage qui permet les
            <u><a href="https://en.wikipedia.org/wiki/Proof-carrying_code">preuves de code</a></u>,
            qui sont la fondation du projet. Sur la blockchain Cardano, le niveau de sécurité est <b>prouvé</b>.
        </p>


        <p>
            Cardano s'est constituée, en quelques années seulement, une communauté de plus de <b>490 000 membres</b>. C'est <a href="">le projet crypto le plus actif
                sur github en 2019</a>. Sa cryptomonnaie, ADA, s'est élevée dans le <b>top10 mondial</b> des cryptomonnaies par
            capitalisation, en seulement 3 ans.
        </p>

        <h2>Cardano et ADA en détails</h2>
        <p>
            Ces pages de documentation sont consacrées à Cardano. Suivez ces liens pour apprendre tout sur ceux qui sont derrière le projet Cardano, ses ambitions,
            sa feuille de route, les défis qui sont sur le chemin, sa cryptomonnaie et le staking, et bien d'autres choses encore.
        </p>

        <h3>Cardano</h3>

        <p>Le projet Cardano, sa gourvenance, son développement, et le fonctionnement de la blockchain.</p>

        <div class="row">
            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/le-developpement-de-cardano">
                            Le développement de Cardano
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/les-phases-de-cardano-roadmap">
                            Les phases de Cardano (roadmap)
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/les-defis-de-cardano">
                            Les défis de route de Cardano
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/qu-est-ce-que-le-delegated-proof-of-stake-dpos">
                            La preuve d'enjeu déléguée
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <h3 class="mt-4">ADA et le staking</h3>
        <p>
            La cryptomonnaie ADA et le <b>staking</b> sur Cardano (ie: comment gagner des interêts en participant au fonctionnement de la blockchain).
        </p>

        <div class="row">
            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/qu-est-ce-que-la-cryptomonnaie-ADA">
                            Qu'est ce qu'ADA ?
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/qu-est-ce-que-le-staking">
                            Qu'est-ce que le staking ?
                        </a>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-3">
                <div class="card card-link">
                    <div class="card-body">
                        <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool">
                            Qu'est-ce qu'une Stake Pool ?
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <div class="button-line mt-5">
            <a class="btn btn-outline-primary btn-right" href="/doc-cardano/le-developpement-de-cardano">
                <i>Suivant:</i>
                <b>Le développement Cardano</b>
                <i class="fa fa-chevron-right"></i>
            </a>
        </div>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title: "Qu'est-ce que Cardano - Stakepool.fr",
        description:
            "Découvrez la blockchain Cardano, sa gouvernance, ses étapes de développement et ses mécanismes"
    })
};
</script>